import { useEffect, useState } from "react";
import {
  Calendar,
  Clock,
  FileText,
  AlertTriangle,
  Send,
  X,
  Upload,
  Download,
  Eye,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";

export default function LeaveRequestForm({
  isOpen,
  onClose,
  leaveTypes,
  onSubmit,
  editRequest = null, // Add prop for editing existing request
  employees = [], // List of employees for selection
  currentUserRole = 'employe', // Current user role
}) {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    employe_id: "",
    type_conge_id: "",
    date_debut: "",
    date_fin: "",
    motif: "",
    urgence: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [calculatedDays, setCalculatedDays] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(false);

  // Pre-fill form when editing
  useEffect(() => {
    if (editRequest && isOpen) {
      setFormData({
        employe_id: editRequest.employe_id || "",
        type_conge_id:
          editRequest.type_conge_id || editRequest.type_conge?.id || "",
        date_debut: editRequest.date_debut
          ? editRequest.date_debut.split("T")[0]
          : "",
        date_fin: editRequest.date_fin
          ? editRequest.date_fin.split("T")[0]
          : "",
        motif: editRequest.motif || "",
        urgence: Boolean(editRequest.urgence),
      });

      // Calculate days for existing request
      if (editRequest.date_debut && editRequest.date_fin) {
        const days = calculateWorkingDays(
          editRequest.date_debut,
          editRequest.date_fin
        );
        setCalculatedDays(days);
      }

      // Clear file selection when editing (user can upload new file if needed)
      setSelectedFile(null);
    } else if (!editRequest && isOpen) {
      // Reset form for new request
      setFormData({
        employe_id: currentUserRole === 'employe' ? "" : "", // Will be set by user selection or auto-filled
        type_conge_id: "",
        date_debut: "",
        date_fin: "",
        motif: "",
        urgence: false,
      });
      setCalculatedDays(0);
      setSelectedFile(null);
      setLeaveBalance(null);
    }
  }, [editRequest, isOpen, currentUserRole]);

  // Load leave balance when employee is selected
  const loadLeaveBalance = async (employeeId) => {
    if (!employeeId) {
      setLeaveBalance(null);
      return;
    }

    setLoadingBalance(true);
    try {
      const response = await fetch(`/api/v1/leave/balance/${employeeId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setLeaveBalance(data.data);
      } else {
        setLeaveBalance(null);
      }
    } catch (error) {
      console.error('Error loading leave balance:', error);
      setLeaveBalance(null);
    } finally {
      setLoadingBalance(false);
    }
  };

  // Handle employee selection
  const handleEmployeeChange = (employeeId) => {
    setFormData({ ...formData, employe_id: employeeId });
    loadLeaveBalance(employeeId);
  };

  // Calculate working days between two dates
  const calculateWorkingDays = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (start >= end) return 0;

    let count = 0;
    const current = new Date(start);

    while (current <= end) {
      const dayOfWeek = current.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        // Not Sunday (0) or Saturday (6)
        count++;
      }
      current.setDate(current.getDate() + 1);
    }

    return count;
  };

  // Update calculated days when dates change
  const handleDateChange = (field, value) => {
    const newFormData = { ...formData, [field]: value };
    setFormData(newFormData);

    if (newFormData.date_debut && newFormData.date_fin) {
      const days = calculateWorkingDays(
        newFormData.date_debut,
        newFormData.date_fin
      );
      setCalculatedDays(days);
    }
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "Erreur",
          description: "La taille du fichier ne doit pas dépasser 5MB",
          variant: "destructive",
        });
        return;
      }

      // Validate file type
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: "Erreur",
          description:
            "Seuls les fichiers PDF, JPG, PNG, DOC et DOCX sont autorisés",
          variant: "destructive",
        });
        return;
      }

      setSelectedFile(file);
    }
  };

  // Remove selected file
  const removeFile = () => {
    setSelectedFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if employee is selected (required for managers/HR)
    if (currentUserRole !== 'employe' && !formData.employe_id) {
      toast({
        title: "Erreur",
        description: "Veuillez sélectionner un employé",
        variant: "destructive",
      });
      return;
    }

    if (!formData.type_conge_id || !formData.date_debut || !formData.date_fin) {
      toast({
        title: "Erreur",
        description: "Veuillez remplir tous les champs obligatoires",
        variant: "destructive",
      });
      return;
    }

    if (new Date(formData.date_debut) >= new Date(formData.date_fin)) {
      toast({
        title: "Erreur",
        description: "La date de fin doit être postérieure à la date de début",
        variant: "destructive",
      });
      return;
    }

    // Check leave balance
    if (leaveBalance && calculatedDays > leaveBalance.remainingDays) {
      toast({
        title: "Erreur",
        description: `Solde insuffisant. Jours restants: ${leaveBalance.remainingDays}`,
        variant: "destructive",
      });
      return;
    }

    // Only check past dates for new requests or when changing dates for existing requests
    // Removed restriction to allow past leave requests

    setSubmitting(true);

    try {
      // Create FormData for file upload
      const formDataToSend = new FormData();
      if (formData.employe_id) {
        formDataToSend.append("employe_id", formData.employe_id);
      }
      formDataToSend.append("type_conge_id", formData.type_conge_id);
      formDataToSend.append("date_debut", formData.date_debut);
      formDataToSend.append("date_fin", formData.date_fin);
      formDataToSend.append("motif", formData.motif);
      formDataToSend.append("urgence", formData.urgence);
      formDataToSend.append("nombre_jours", calculatedDays);

      // Add file if selected
      if (selectedFile) {
        formDataToSend.append("justification", selectedFile);
      }

      // Pass the edit request ID if editing
      await onSubmit(formDataToSend, editRequest?.id);

      toast({
        title: "Succès",
        description: editRequest
          ? "Votre demande de congé a été mise à jour avec succès"
          : "Votre demande de congé a été soumise avec succès",
      });

      // Reset form only if not editing or after successful edit
      if (!editRequest) {
        setFormData({
          employe_id: currentUserRole === 'employe' ? "" : "",
          type_conge_id: "",
          date_debut: "",
          date_fin: "",
          motif: "",
          urgence: false,
        });
        setCalculatedDays(0);
        setSelectedFile(null);
        setLeaveBalance(null);
      }
      onClose();
    } catch (error) {
      toast({
        title: "Erreur",
        description:
          error.message ||
          (editRequest
            ? "Erreur lors de la mise à jour de la demande"
            : "Erreur lors de la soumission de la demande"),
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const selectedLeaveType = leaveTypes.find(
    (type) => type.id === formData.type_conge_id
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Calendar className="h-6 w-6 text-blue-600" />
            {editRequest
              ? "Modifier la demande de congé"
              : "Nouvelle demande de congé"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Employee Selection (for managers/HR) */}
          {currentUserRole !== 'employe' && (
            <div className="space-y-2">
              <Label htmlFor="employe_id" className="text-sm font-medium">
                Employé *
              </Label>
              <Select
                value={formData.employe_id}
                onValueChange={handleEmployeeChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionnez un employé" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((employee) => (
                    <SelectItem key={employee.id} value={employee.id}>
                      {employee.prenom} {employee.nom} ({employee.numero_employe})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Leave Balance Display */}
          {leaveBalance && (
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-4">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-sm text-gray-600">Jours acquis</div>
                    <div className="text-lg font-semibold text-blue-600">
                      {leaveBalance.accruedDays}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Jours consommés</div>
                    <div className="text-lg font-semibold text-orange-600">
                      {leaveBalance.usedDays}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Solde restant</div>
                    <div className={`text-lg font-semibold ${leaveBalance.remainingDays < calculatedDays
                      ? 'text-red-600'
                      : 'text-green-600'
                      }`}>
                      {leaveBalance.remainingDays}
                    </div>
                  </div>
                </div>
                {calculatedDays > 0 && (
                  <div className="mt-2 text-sm text-center">
                    Jours demandés: <span className="font-semibold">{calculatedDays}</span>
                    {calculatedDays > leaveBalance.remainingDays && (
                      <span className="text-red-600 ml-2">(Solde insuffisant)</span>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Leave Type Selection */}
          <div className="space-y-2">
            <Label htmlFor="type_conge_id" className="text-sm font-medium">
              Type de congé *
            </Label>
            <Select
              value={formData.type_conge_id}
              onValueChange={(value) =>
                setFormData({ ...formData, type_conge_id: value })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Sélectionnez un type de congé" />
              </SelectTrigger>
              <SelectContent>
                {leaveTypes.map((type) => (
                  <SelectItem key={type.id} value={type.id}>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded"
                        style={{ backgroundColor: type.couleur }}
                      ></div>
                      <span>{type.nom}</span>
                      {type.jours_autorises_par_an && (
                        <Badge variant="outline" className="text-xs">
                          {type.jours_autorises_par_an} jours/an
                        </Badge>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Date Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="date_debut" className="text-sm font-medium">
                Date de début *
              </Label>
              <Input
                id="date_debut"
                type="date"
                value={formData.date_debut}
                onChange={(e) => handleDateChange("date_debut", e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="date_fin" className="text-sm font-medium">
                Date de fin *
              </Label>
              <Input
                id="date_fin"
                type="date"
                value={formData.date_fin}
                onChange={(e) => handleDateChange("date_fin", e.target.value)}
                required
              />
            </div>
          </div>

          {/* Duration Display & Manual Adjustment */}
          <div className="space-y-2">
            <Label htmlFor="calculatedDays" className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4 text-blue-600" />
              Nombre de jours *
            </Label>
            <div className="flex items-center gap-4">
              <Input
                id="calculatedDays"
                type="number"
                step="0.5"
                min="0.5"
                value={calculatedDays}
                onChange={(e) => setCalculatedDays(parseFloat(e.target.value) || 0)}
                className="w-32"
                required
              />
              <span className="text-sm text-gray-500 italic">
                (Calculé automatiquement: {calculateWorkingDays(formData.date_debut, formData.date_fin)} jours ouvrables)
              </span>
            </div>
            {selectedLeaveType?.jours_autorises_par_an && (
              <p className="text-xs text-blue-600 mt-1">
                Limite annuelle pour ce type: {selectedLeaveType.jours_autorises_par_an} jours
              </p>
            )}
          </div>

          {/* Reason */}
          <div className="space-y-2">
            <Label htmlFor="motif" className="text-sm font-medium">
              Motif (optionnel)
            </Label>
            <Textarea
              id="motif"
              placeholder="Décrivez la raison de votre demande de congé..."
              value={formData.motif}
              onChange={(e) =>
                setFormData({ ...formData, motif: e.target.value })
              }
              className="min-h-[100px]"
            />
          </div>

          {/* File Upload for Justification */}
          <div className="space-y-2">
            <Label htmlFor="justification" className="text-sm font-medium">
              Justificatif (optionnel)
            </Label>

            {/* Show existing file when editing */}
            {editRequest && editRequest.justification && !selectedFile && (
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-green-600" />
                      <div>
                        <p className="text-sm font-medium text-green-800">
                          Fichier existant:{" "}
                          {editRequest.justification.fileName || "Document.pdf"}
                        </p>
                        <p className="text-xs text-green-600">
                          {editRequest.justification.fileSize
                            ? `${(
                              editRequest.justification.fileSize /
                              1024 /
                              1024
                            ).toFixed(2)} MB`
                            : "Taille inconnue"}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          // Download existing file
                          window.open(
                            `/api/leaves/requests/${editRequest.id}/justification/download`,
                            "_blank"
                          );
                        }}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs text-green-600 mt-2">
                    Vous pouvez télécharger un nouveau fichier pour remplacer
                    celui-ci
                  </p>
                </CardContent>
              </Card>
            )}

            <div className="space-y-3">
              {!selectedFile ? (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                  <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <div className="space-y-1">
                    <p className="text-sm text-gray-600">
                      Glissez-déposez un fichier ou{" "}
                      <label
                        htmlFor="justification"
                        className="text-blue-600 hover:text-blue-700 cursor-pointer font-medium"
                      >
                        parcourez
                      </label>
                    </p>
                    <p className="text-xs text-gray-500">
                      PDF, JPG, PNG, DOC, DOCX jusqu'à 5MB
                    </p>
                  </div>
                  <Input
                    id="justification"
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </div>
              ) : (
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <FileText className="h-5 w-5 text-blue-600" />
                        <div>
                          <p className="text-sm font-medium text-blue-800">
                            {selectedFile.name}
                          </p>
                          <p className="text-xs text-blue-600">
                            {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={removeFile}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>

          {/* Urgency */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="urgence"
              checked={formData.urgence}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, urgence: checked })
              }
            />
            <Label
              htmlFor="urgence"
              className="text-sm font-medium flex items-center gap-2"
            >
              <AlertTriangle className="h-4 w-4 text-orange-500" />
              Demande urgente
            </Label>
          </div>

          {formData.urgence && (
            <Card className="bg-orange-50 border-orange-200">
              <CardContent className="p-4">
                <p className="text-sm text-orange-800">
                  <strong>Note:</strong> Les demandes urgentes nécessitent une
                  approbation immédiate et peuvent être traitées en priorité par
                  votre manager.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Form Actions */}
          <DialogFooter className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={submitting}
            >
              <X className="h-4 w-4 mr-2" />
              Annuler
            </Button>
            <Button type="submit" disabled={submitting || calculatedDays === 0}>
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                  {editRequest ? "Mise à jour..." : "Envoi en cours..."}
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  {editRequest ? "Mettre à jour" : "Soumettre la demande"}
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
